/**
 * Polia zákazky — rovnaké v okne Nová zákazka aj Upraviť zákazku (KOLO 162).
 *
 * User: *„pridávanie zákazky a editovanie sa nezhoduje. Ten termín dodania
 * nepotrebujem ani klienta, to môžeš odstrániť."* Jeden komponent, aby sa
 * okná znova nerozišli.
 *
 * Pri vytvorení sa vyberá **druh čísla** (KOLO 200): výpočet V, zákazka AA
 * alebo malá zákazka bez AA (Z). Číslo sa píše len pri zákazke AA.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { DRUHY_CISLA, type DruhCisla } from "@/lib/druhCisla";

export interface ZakazkaPoliaHodnoty {
  cislo: string;
  nazov: string;
  bezHran: boolean;
  /** Len pri vytvorení — pri úprave sa druh nemení. */
  druh?: DruhCisla;
}

export function ZakazkaPolia({
  hodnoty,
  onChange,
  idPrefix,
  cisloPovinne,
  trvaleCislo,
}: {
  hodnoty: ZakazkaPoliaHodnoty;
  onChange: (h: ZakazkaPoliaHodnoty) => void;
  idPrefix: string;
  /**
   * Úprava (číslo je povinné). Pri vytvorení (`false`) sa vyberá druh
   * a číslo sa píše len pri zákazke AA.
   */
  cisloPovinne: boolean;
  /** Malá zákazka bez AA — číslo Z sa meniť nedá (KOLO 200). */
  trvaleCislo?: boolean;
}): JSX.Element {
  const druh = hodnoty.druh ?? "vypocet";
  const ukazCislo = cisloPovinne || druh === "zakazka";
  return (
    <>
      {!cisloPovinne ? (
        <fieldset className="space-y-1.5">
          <legend className="mb-1.5 text-sm font-medium">Druh</legend>
          {DRUHY_CISLA.map((d) => (
            <label
              key={d.druh}
              className={cn(
                "flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm",
                druh === d.druh ? "border-primary bg-tandem-red-light" : "border-input",
              )}
            >
              <input
                type="radio"
                name={`${idPrefix}-druh`}
                value={d.druh}
                checked={druh === d.druh}
                onChange={() => onChange({ ...hodnoty, druh: d.druh })}
                className="mt-0.5 h-4 w-4 accent-primary"
              />
              <span>
                <span className="font-medium">{d.nazov}</span>
                <span className="block text-xs text-muted-foreground">{d.popis}</span>
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}

      {ukazCislo ? (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-cislo`}>
            Číslo zákazky{trvaleCislo ? "" : " *"}
          </Label>
          <Input
            id={`${idPrefix}-cislo`}
            autoFocus={!trvaleCislo}
            value={hodnoty.cislo}
            readOnly={trvaleCislo}
            placeholder={cisloPovinne ? undefined : "napr. AA6319"}
            onChange={(e) => onChange({ ...hodnoty, cislo: e.target.value })}
            className={cn(trvaleCislo && "bg-muted")}
          />
          {trvaleCislo ? (
            <p className="text-xs text-muted-foreground">
              Číslo malej zákazky bez AA je trvalé — iné číslo nedostane.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-nazov`}>Názov</Label>
        <Input
          id={`${idPrefix}-nazov`}
          autoFocus={!ukazCislo}
          value={hodnoty.nazov}
          placeholder="Predajňa PMS Sladkovičovo"
          onChange={(e) => onChange({ ...hodnoty, nazov: e.target.value })}
        />
      </div>

      {/* KOLO 131: ručne zadaný 500×400 s hranou 1 mm ide ďalej ako
          502×402 — rozmer s hranou, ako ho dáva TopSolid */}
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={hodnoty.bezHran}
          onChange={(e) => onChange({ ...hodnoty, bezHran: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-primary"
        />
        <span>
          Ručne zadávané rozmery sú bez hrán
          <span className="block text-xs text-muted-foreground">
            K zadanému rozmeru dielca sa pripočítajú hrany (500×400 s 1 mm
            hranou → 502×402). Pri zákazke z TopSolidu vypnuté — tam rozmer
            hranu už obsahuje.
          </span>
        </span>
      </label>
    </>
  );
}
